/**
 * Enterprise Roster Service
 * Comprehensive workforce management: leave, timesheets, shift marketplace,
 * on-call & escalation, acuity demand, payroll integration, GPMS reporting,
 * workforce analytics, demand forecasting, multi-facility orchestration
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  LEAVE_TYPES,
  AWARD_DEFINITIONS,
  SHIFT_MARKETPLACE_TIERS,
  TIMESHEET_STATUSES,
  VARIANCE_TYPES,
  ESCALATION_LEVELS,
  EMPLOYMENT_TYPES,
  createLeaveRequest,
  createTimesheetEntry,
  createMarketplaceShift,
  createOnCallEntry,
  createEscalationEvent,
  createFacilityStaffingProfile,
  calculateAwardPay,
  calculateDemand
} from '../../data/models/EnterpriseRosterModels';
import HRService from '../hr/HRService';

const STORAGE_KEYS = {
  LEAVE_REQUESTS: 'enterprise_leave_requests',
  LEAVE_BALANCES: 'enterprise_leave_balances',
  BLACKOUT_PERIODS: 'enterprise_blackout_periods',
  CLOCK_RECORDS: 'enterprise_clock_records',
  TIMESHEETS: 'enterprise_timesheets',
  MARKETPLACE: 'enterprise_marketplace',
  ONCALL: 'enterprise_oncall',
  ESCALATIONS: 'enterprise_escalations',
  DEMAND_CACHE: 'enterprise_demand_cache',
  PAY_RUNS: 'enterprise_pay_runs',
  GPMS_REPORTS: 'enterprise_gpms_reports',
  ANALYTICS_CACHE: 'enterprise_analytics_cache',
  FACILITY_PROFILES: 'enterprise_facility_profiles'
};

const GEO_FENCE_RADIUS_METRES = 200;
const MAX_CONCURRENT_LEAVE_RATIO = 0.2;

const _uid = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const _round = (n) => Math.round(n * 100) / 100;
const _toDateStr = (d) => (d instanceof Date ? d : new Date(d)).toISOString().split('T')[0];
const _daysBetween = (a, b) => Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)) + 1;

class EnterpriseRosterService {
  constructor() {
    this.initialized = false;
    this._initializeMockData();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Initialization
  // ═══════════════════════════════════════════════════════════════════════════

  _initializeMockData() {
    const existing = LocalStorageService.get(STORAGE_KEYS.LEAVE_BALANCES, []);
    if (existing.length > 0) {
      this.initialized = true;
      return;
    }

    console.log('[EnterpriseRosterService] Initializing mock data...');
    const now = new Date();

    this._seedFacilityProfiles();
    this._seedLeaveBalances(now);
    this._seedLeaveRequests(now);
    this._seedBlackoutPeriods(now);
    this._seedClockRecords(now);
    this._seedMarketplaceShifts(now);
    this._seedOnCallEntries(now);

    this.initialized = true;
    console.log('[EnterpriseRosterService] Mock data initialisation complete');
  }

  _seedFacilityProfiles() {
    const profiles = [
      createFacilityStaffingProfile({
        id: 'fsp-sunshine', facilityId: 'sunshine', facilityName: 'Sunshine Gardens',
        totalBeds: 90, currentOccupancy: 82, region: 'Metro West', stateJurisdiction: 'VIC',
        wardProfiles: [
          { wardId: 'ward-a', wardName: 'Ward A', beds: 30, occupancy: 28, acuityMix: { highNeed: 12, lowNeed: 16 } },
          { wardId: 'ward-b', wardName: 'Ward B', beds: 30, occupancy: 27, acuityMix: { highNeed: 10, lowNeed: 17 } },
          { wardId: 'dementia', wardName: 'Dementia Unit', beds: 30, occupancy: 27, acuityMix: { highNeed: 20, lowNeed: 7 } }
        ],
        publicHolidayCalendar: ['2026-01-01', '2026-01-26', '2026-04-03', '2026-04-04', '2026-04-06', '2026-04-25', '2026-06-08', '2026-11-03', '2026-12-25', '2026-12-26']
      }),
      createFacilityStaffingProfile({
        id: 'fsp-bayview', facilityId: 'bayview', facilityName: 'Bayview Gardens',
        totalBeds: 60, currentOccupancy: 55, region: 'Metro East', stateJurisdiction: 'VIC',
        wardProfiles: [
          { wardId: 'ward-1', wardName: 'Ward 1', beds: 30, occupancy: 28, acuityMix: { highNeed: 11, lowNeed: 17 } },
          { wardId: 'ward-2', wardName: 'Ward 2', beds: 30, occupancy: 27, acuityMix: { highNeed: 9, lowNeed: 18 } }
        ],
        publicHolidayCalendar: ['2026-01-01', '2026-01-26', '2026-04-03', '2026-04-04', '2026-04-06', '2026-04-25', '2026-06-08', '2026-11-03', '2026-12-25', '2026-12-26']
      })
    ];
    profiles.forEach(p => LocalStorageService.addItem(STORAGE_KEYS.FACILITY_PROFILES, p));
  }

  _seedLeaveBalances(now) {
    const staffIds = [
      { id: 'staff-james-chen', name: 'James Chen', role: 'RN', facility: 'sunshine' },
      { id: 'staff-emma-thompson', name: 'Emma Thompson', role: 'RN', facility: 'sunshine' },
      { id: 'staff-david-rodriguez', name: 'David Rodriguez', role: 'EN', facility: 'sunshine' },
      { id: 'staff-michael-brown', name: 'Michael Brown', role: 'PCA', facility: 'sunshine' },
      { id: 'staff-sophie-wilson', name: 'Sophie Wilson', role: 'PCA', facility: 'sunshine' },
      { id: 'staff-grace-lee', name: 'Grace Lee', role: 'RN', facility: 'bayview' },
      { id: 'staff-daniel-wang', name: 'Daniel Wang', role: 'EN', facility: 'bayview' },
      { id: 'staff-hannah-smith', name: 'Hannah Smith', role: 'PCA', facility: 'bayview' }
    ];

    staffIds.forEach(s => {
      LocalStorageService.addItem(STORAGE_KEYS.LEAVE_BALANCES, {
        id: `bal-${s.id}`,
        staffId: s.id,
        staffName: s.name,
        facilityId: s.facility,
        balances: {
          annual: { accrued: 120 + Math.floor(Math.random() * 40), taken: 24 + Math.floor(Math.random() * 20), pending: 0 },
          personal: { accrued: 76, taken: 8 + Math.floor(Math.random() * 16), pending: 0 },
          long_service: { accrued: s.id.includes('chen') ? 380 : 0, taken: 0, pending: 0 },
          compassionate: { accrued: 15.2, taken: 0, pending: 0 },
          study: { accrued: 38, taken: Math.floor(Math.random() * 16), pending: 0 },
          toil: { accrued: Math.floor(Math.random() * 24), taken: 0, pending: 0 }
        },
        lastAccrualDate: _toDateStr(now),
        updatedAt: now.toISOString()
      });
    });
    console.log(`[EnterpriseRosterService] Seeded leave balances for ${staffIds.length} staff`);
  }

  _seedLeaveRequests(now) {
    const requests = [
      { staffId: 'staff-james-chen', staffName: 'James Chen', leaveType: 'annual', startDate: '2026-04-20', endDate: '2026-04-24', status: 'approved', hoursDeducted: 40, approvedBy: 'Sarah Mitchell', approvedAt: new Date(now.getTime() - 5 * 86400000).toISOString() },
      { staffId: 'staff-emma-thompson', staffName: 'Emma Thompson', leaveType: 'personal', startDate: '2026-04-14', endDate: '2026-04-14', status: 'pending', hoursDeducted: 8, medicalCertificateAttached: true },
      { staffId: 'staff-sophie-wilson', staffName: 'Sophie Wilson', leaveType: 'annual', startDate: '2026-05-04', endDate: '2026-05-08', status: 'pending', hoursDeducted: 40 },
      { staffId: 'staff-grace-lee', staffName: 'Grace Lee', leaveType: 'study', startDate: '2026-04-15', endDate: '2026-04-15', status: 'approved', hoursDeducted: 8, approvedBy: 'Facility Admin', approvedAt: new Date(now.getTime() - 3 * 86400000).toISOString() },
      { staffId: 'staff-michael-brown', staffName: 'Michael Brown', leaveType: 'annual', startDate: '2026-03-10', endDate: '2026-03-14', status: 'approved', hoursDeducted: 40, approvedBy: 'Sarah Mitchell', approvedAt: new Date(now.getTime() - 30 * 86400000).toISOString(), payrollExported: true }
    ];
    requests.forEach(r => LocalStorageService.addItem(STORAGE_KEYS.LEAVE_REQUESTS, createLeaveRequest(r)));
    console.log(`[EnterpriseRosterService] Seeded ${requests.length} leave requests`);
  }

  _seedBlackoutPeriods(now) {
    const blackouts = [
      { id: 'bo-xmas-sunshine', facilityId: 'sunshine', startDate: '2026-12-20', endDate: '2026-12-31', reason: 'Christmas / New Year peak period', createdBy: 'Sarah Mitchell' },
      { id: 'bo-easter-sunshine', facilityId: 'sunshine', startDate: '2026-04-03', endDate: '2026-04-06', reason: 'Easter long weekend', createdBy: 'Sarah Mitchell' },
      { id: 'bo-xmas-bayview', facilityId: 'bayview', startDate: '2026-12-20', endDate: '2026-12-31', reason: 'Christmas / New Year peak period', createdBy: 'Admin' }
    ];
    blackouts.forEach(b => LocalStorageService.addItem(STORAGE_KEYS.BLACKOUT_PERIODS, { ...b, createdAt: now.toISOString() }));
  }

  _seedClockRecords(now) {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = _toDateStr(yesterday);

    const records = [
      { staffId: 'staff-james-chen', staffName: 'James Chen', shiftId: `roster-${dateStr}-sunshine-0`, facilityId: 'sunshine', clockIn: `${dateStr}T05:55:00`, clockOut: `${dateStr}T14:05:00`, location: { lat: -37.789, lng: 144.922, verified: true } },
      { staffId: 'staff-emma-thompson', staffName: 'Emma Thompson', shiftId: `roster-${dateStr}-sunshine-1`, facilityId: 'sunshine', clockIn: `${dateStr}T13:50:00`, clockOut: `${dateStr}T22:10:00`, location: { lat: -37.789, lng: 144.922, verified: true } },
      { staffId: 'staff-michael-brown', staffName: 'Michael Brown', shiftId: `roster-${dateStr}-sunshine-2`, facilityId: 'sunshine', clockIn: `${dateStr}T06:02:00`, clockOut: `${dateStr}T14:00:00`, location: { lat: -37.789, lng: 144.923, verified: true } },
      { staffId: 'staff-grace-lee', staffName: 'Grace Lee', shiftId: `roster-${dateStr}-bayview-0`, facilityId: 'bayview', clockIn: `${dateStr}T05:58:00`, clockOut: `${dateStr}T14:03:00`, location: { lat: -37.850, lng: 145.010, verified: true } }
    ];
    records.forEach(r => LocalStorageService.addItem(STORAGE_KEYS.CLOCK_RECORDS, { id: _uid('clk'), ...r, status: 'completed', createdAt: now.toISOString() }));
    console.log(`[EnterpriseRosterService] Seeded ${records.length} clock records`);
  }

  _seedMarketplaceShifts(now) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = _toDateStr(tomorrow);
    const dayAfter = new Date(now);
    dayAfter.setDate(dayAfter.getDate() + 2);
    const dayAfterStr = _toDateStr(dayAfter);

    const shifts = [
      createMarketplaceShift({
        shiftId: `roster-${tomorrowStr}-sunshine-open-1`, facilityId: 'sunshine', facilityName: 'Sunshine Gardens',
        tier: 'casual_pool', currentTier: 'casual_pool', status: 'open',
        applicants: [
          { staffId: 'staff-lisa-park', staffName: 'Lisa Park', appliedAt: now.toISOString(), roleCode: 'EN', eligible: true }
        ]
      }),
      createMarketplaceShift({
        shiftId: `roster-${tomorrowStr}-sunshine-open-2`, facilityId: 'sunshine', facilityName: 'Sunshine Gardens',
        tier: 'float_pool', currentTier: 'float_pool', status: 'open'
      }),
      createMarketplaceShift({
        shiftId: `roster-${dayAfterStr}-bayview-open-1`, facilityId: 'bayview', facilityName: 'Bayview Gardens',
        tier: 'pt_extras', currentTier: 'pt_extras', status: 'open'
      })
    ];
    shifts.forEach(s => LocalStorageService.addItem(STORAGE_KEYS.MARKETPLACE, s));
    console.log(`[EnterpriseRosterService] Seeded ${shifts.length} marketplace shifts`);
  }

  _seedOnCallEntries(now) {
    const tonight = new Date(now);
    tonight.setHours(22, 0, 0, 0);
    const tomorrowMorning = new Date(tonight);
    tomorrowMorning.setHours(tonight.getHours() + 8);

    const entries = [
      createOnCallEntry({
        staffId: 'staff-james-chen', staffName: 'James Chen', facilityId: 'sunshine',
        startDateTime: tonight.toISOString(), endDateTime: tomorrowMorning.toISOString(),
        type: 'weekday', allowanceRate: 18.68, status: 'active'
      }),
      createOnCallEntry({
        staffId: 'staff-grace-lee', staffName: 'Grace Lee', facilityId: 'bayview',
        startDateTime: tonight.toISOString(), endDateTime: tomorrowMorning.toISOString(),
        type: 'weekday', allowanceRate: 18.68, status: 'scheduled'
      })
    ];
    entries.forEach(e => LocalStorageService.addItem(STORAGE_KEYS.ONCALL, e));
    console.log(`[EnterpriseRosterService] Seeded ${entries.length} on-call entries`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. Leave Management
  // ═══════════════════════════════════════════════════════════════════════════

  async submitLeaveRequest(data) {
    try {
      console.log(`[EnterpriseRosterService] Leave request: ${data.staffName || data.staffId} — ${data.leaveType} ${data.startDate}→${data.endDate}`);

      const leaveType = LEAVE_TYPES[data.leaveType];
      if (!leaveType) return { success: false, error: `Unknown leave type: ${data.leaveType}` };

      const hoursRequested = data.hoursDeducted || _daysBetween(data.startDate, data.endDate) * 8;

      const balances = LocalStorageService.find(STORAGE_KEYS.LEAVE_BALANCES, b => b.staffId === data.staffId);
      const balanceRecord = balances[0];
      if (balanceRecord && leaveType.entitlementHoursPA) {
        const bal = balanceRecord.balances[data.leaveType];
        if (bal) {
          const available = bal.accrued - bal.taken - bal.pending;
          if (hoursRequested > available) {
            return { success: false, error: `Insufficient ${leaveType.label} balance: ${available}h available, ${hoursRequested}h requested` };
          }
        }
      }

      const blackouts = LocalStorageService.find(STORAGE_KEYS.BLACKOUT_PERIODS,
        b => b.facilityId === data.facilityId && data.startDate <= b.endDate && data.endDate >= b.startDate
      );
      if (blackouts.length > 0 && !data.blackoutOverride) {
        return { success: false, error: `Leave dates overlap blackout period: ${blackouts[0].reason} (${blackouts[0].startDate} – ${blackouts[0].endDate})` };
      }

      const concurrentLeave = LocalStorageService.find(STORAGE_KEYS.LEAVE_REQUESTS,
        r => r.status === 'approved' && r.startDate <= data.endDate && r.endDate >= data.startDate
      );
      const hrResult = await HRService.getAll(data.facilityId);
      const totalStaff = hrResult.success ? hrResult.data.length : 10;
      if (concurrentLeave.length >= Math.ceil(totalStaff * MAX_CONCURRENT_LEAVE_RATIO)) {
        return { success: false, error: `Concurrent leave limit reached: ${concurrentLeave.length} staff already on leave during this period` };
      }

      const request = createLeaveRequest({
        ...data,
        hoursDeducted: hoursRequested,
        balanceBefore: balanceRecord?.balances?.[data.leaveType]?.accrued - (balanceRecord?.balances?.[data.leaveType]?.taken || 0),
        status: 'pending',
        rosterImpact: { shiftsAffected: [], backfillRequired: true, backfillTaskId: null }
      });

      const created = LocalStorageService.addItem(STORAGE_KEYS.LEAVE_REQUESTS, request);

      if (balanceRecord) {
        const bal = balanceRecord.balances[data.leaveType];
        if (bal) {
          bal.pending = (bal.pending || 0) + hoursRequested;
          LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_BALANCES, balanceRecord.id, { balances: balanceRecord.balances });
        }
      }

      return { success: true, data: created };
    } catch (error) {
      console.error('[EnterpriseRosterService] submitLeaveRequest error:', error);
      return { success: false, error: error.message };
    }
  }

  async approveLeaveRequest(requestId, approvedBy) {
    try {
      const request = LocalStorageService.findById(STORAGE_KEYS.LEAVE_REQUESTS, requestId);
      if (!request) return { success: false, error: 'Leave request not found' };
      if (request.status !== 'pending') return { success: false, error: `Cannot approve request in status: ${request.status}` };

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_REQUESTS, requestId, {
        status: 'approved', approvedBy, approvedAt: new Date().toISOString(),
        balanceAfter: (request.balanceBefore || 0) - request.hoursDeducted
      });

      const balances = LocalStorageService.find(STORAGE_KEYS.LEAVE_BALANCES, b => b.staffId === request.staffId);
      if (balances[0]) {
        const bal = balances[0].balances[request.leaveType];
        if (bal) {
          bal.taken += request.hoursDeducted;
          bal.pending = Math.max(0, (bal.pending || 0) - request.hoursDeducted);
          LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_BALANCES, balances[0].id, { balances: balances[0].balances });
        }
      }

      const backfillTaskId = _uid('backfill');
      LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_REQUESTS, requestId, {
        rosterImpact: { ...request.rosterImpact, backfillRequired: true, backfillTaskId }
      });

      console.log(`[EnterpriseRosterService] Leave approved: ${requestId} by ${approvedBy}, backfill task ${backfillTaskId}`);
      return { success: true, data: { ...updated, backfillTaskId } };
    } catch (error) {
      console.error('[EnterpriseRosterService] approveLeaveRequest error:', error);
      return { success: false, error: error.message };
    }
  }

  async declineLeaveRequest(requestId, reason, declinedBy) {
    try {
      const request = LocalStorageService.findById(STORAGE_KEYS.LEAVE_REQUESTS, requestId);
      if (!request) return { success: false, error: 'Leave request not found' };
      if (request.status !== 'pending') return { success: false, error: `Cannot decline request in status: ${request.status}` };

      const balances = LocalStorageService.find(STORAGE_KEYS.LEAVE_BALANCES, b => b.staffId === request.staffId);
      if (balances[0]) {
        const bal = balances[0].balances[request.leaveType];
        if (bal) {
          bal.pending = Math.max(0, (bal.pending || 0) - request.hoursDeducted);
          LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_BALANCES, balances[0].id, { balances: balances[0].balances });
        }
      }

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.LEAVE_REQUESTS, requestId, {
        status: 'declined', declinedReason: reason, approvedBy: declinedBy, approvedAt: new Date().toISOString()
      });
      console.log(`[EnterpriseRosterService] Leave declined: ${requestId} — ${reason}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error('[EnterpriseRosterService] declineLeaveRequest error:', error);
      return { success: false, error: error.message };
    }
  }

  async getLeaveRequests(filters = {}) {
    try {
      let requests = LocalStorageService.get(STORAGE_KEYS.LEAVE_REQUESTS, []);
      if (filters.staffId) requests = requests.filter(r => r.staffId === filters.staffId);
      if (filters.facilityId) requests = requests.filter(r => r.facilityId === filters.facilityId);
      if (filters.status) requests = requests.filter(r => r.status === filters.status);
      if (filters.startDate) requests = requests.filter(r => r.endDate >= filters.startDate);
      if (filters.endDate) requests = requests.filter(r => r.startDate <= filters.endDate);
      requests.sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
      return { success: true, data: requests };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getLeaveBalances(staffId) {
    try {
      const records = LocalStorageService.find(STORAGE_KEYS.LEAVE_BALANCES, b => b.staffId === staffId);
      if (records.length === 0) return { success: false, error: 'No leave balance record found' };

      const record = records[0];
      const summary = Object.entries(record.balances).map(([type, bal]) => ({
        leaveType: type,
        label: LEAVE_TYPES[type]?.label || type,
        accrued: bal.accrued,
        taken: bal.taken,
        pending: bal.pending || 0,
        available: _round(bal.accrued - bal.taken - (bal.pending || 0)),
        paidLeave: LEAVE_TYPES[type]?.paidLeave ?? true
      }));

      return { success: true, data: { staffId, staffName: record.staffName, balances: summary, raw: record.balances } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getLeaveCalendar(facilityId, startDate, endDate) {
    try {
      const requests = LocalStorageService.find(STORAGE_KEYS.LEAVE_REQUESTS,
        r => (r.facilityId === facilityId || !r.facilityId) && r.status === 'approved' && r.startDate <= endDate && r.endDate >= startDate
      );

      const calendar = {};
      for (const req of requests) {
        const start = new Date(Math.max(new Date(req.startDate), new Date(startDate)));
        const end = new Date(Math.min(new Date(req.endDate), new Date(endDate)));
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const key = _toDateStr(d);
          if (!calendar[key]) calendar[key] = [];
          calendar[key].push({ staffId: req.staffId, staffName: req.staffName, leaveType: req.leaveType, label: LEAVE_TYPES[req.leaveType]?.label || req.leaveType });
        }
      }

      return { success: true, data: { facilityId, period: { startDate, endDate }, calendar, totalLeaveEvents: requests.length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getBlackoutPeriods(facilityId) {
    try {
      const periods = LocalStorageService.find(STORAGE_KEYS.BLACKOUT_PERIODS, b => b.facilityId === facilityId);
      return { success: true, data: periods };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async setBlackoutPeriod(facilityId, startDate, endDate, reason) {
    try {
      const period = {
        id: _uid('bo'),
        facilityId, startDate, endDate, reason,
        createdAt: new Date().toISOString()
      };
      const created = LocalStorageService.addItem(STORAGE_KEYS.BLACKOUT_PERIODS, period);
      console.log(`[EnterpriseRosterService] Blackout set: ${facilityId} ${startDate}→${endDate} — ${reason}`);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getLeaveLiabilityReport(facilityId) {
    try {
      const allBalances = LocalStorageService.find(STORAGE_KEYS.LEAVE_BALANCES, b => b.facilityId === facilityId);
      const hrResult = await HRService.getAll(facilityId);
      const staffProfiles = hrResult.success ? hrResult.data : [];

      const BASE_HOURLY = 35.00;
      let totalLiability = 0;
      const staffLiabilities = allBalances.map(record => {
        const profile = staffProfiles.find(p => p.id === record.staffId || `${p.firstName} ${p.lastName}` === record.staffName);
        const hourlyRate = profile ? (BASE_HOURLY + (profile.roleCode === 'RN' ? 10 : profile.roleCode === 'EN' ? 3 : 0)) : BASE_HOURLY;

        const liabilityByType = {};
        let staffTotal = 0;
        for (const [type, bal] of Object.entries(record.balances)) {
          if (!LEAVE_TYPES[type]?.paidLeave) continue;
          const unused = bal.accrued - bal.taken;
          if (unused <= 0) continue;
          const dollarValue = _round(unused * hourlyRate);
          liabilityByType[type] = { hours: unused, dollarValue, label: LEAVE_TYPES[type]?.label || type };
          staffTotal += dollarValue;
        }
        totalLiability += staffTotal;

        return { staffId: record.staffId, staffName: record.staffName, hourlyRate, liabilityByType, totalLiability: _round(staffTotal) };
      });

      staffLiabilities.sort((a, b) => b.totalLiability - a.totalLiability);

      return {
        success: true,
        data: {
          facilityId,
          totalLiability: _round(totalLiability),
          staffCount: staffLiabilities.length,
          staffLiabilities,
          byLeaveType: this._aggregateLiabilityByType(staffLiabilities),
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _aggregateLiabilityByType(staffLiabilities) {
    const agg = {};
    for (const s of staffLiabilities) {
      for (const [type, info] of Object.entries(s.liabilityByType)) {
        if (!agg[type]) agg[type] = { label: info.label, totalHours: 0, totalDollars: 0 };
        agg[type].totalHours += info.hours;
        agg[type].totalDollars += info.dollarValue;
      }
    }
    for (const key of Object.keys(agg)) {
      agg[key].totalHours = _round(agg[key].totalHours);
      agg[key].totalDollars = _round(agg[key].totalDollars);
    }
    return agg;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. Timesheet & Attendance
  // ═══════════════════════════════════════════════════════════════════════════

  async clockIn(staffId, shiftId, location = null) {
    try {
      console.log(`[EnterpriseRosterService] Clock-in: ${staffId} shift=${shiftId}`);

      if (location) {
        const facilityCoords = this._getFacilityCoords(location.facilityId);
        if (facilityCoords) {
          const distance = this._haversine(location.lat, location.lng, facilityCoords.lat, facilityCoords.lng);
          if (distance > GEO_FENCE_RADIUS_METRES) {
            return { success: false, error: `Geo-fence violation: ${Math.round(distance)}m from facility (max ${GEO_FENCE_RADIUS_METRES}m)` };
          }
        }
      }

      const existing = LocalStorageService.find(STORAGE_KEYS.CLOCK_RECORDS,
        r => r.staffId === staffId && r.shiftId === shiftId && !r.clockOut
      );
      if (existing.length > 0) return { success: false, error: 'Already clocked in for this shift' };

      const record = {
        id: _uid('clk'), staffId, shiftId,
        facilityId: location?.facilityId || null,
        clockIn: new Date().toISOString(),
        clockOut: null,
        location: location ? { lat: location.lat, lng: location.lng, verified: true } : { verified: false },
        status: 'active',
        createdAt: new Date().toISOString()
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.CLOCK_RECORDS, record);
      return { success: true, data: created };
    } catch (error) {
      console.error('[EnterpriseRosterService] clockIn error:', error);
      return { success: false, error: error.message };
    }
  }

  async clockOut(staffId, shiftId) {
    try {
      const records = LocalStorageService.find(STORAGE_KEYS.CLOCK_RECORDS,
        r => r.staffId === staffId && r.shiftId === shiftId && r.status === 'active'
      );
      if (records.length === 0) return { success: false, error: 'No active clock-in found for this shift' };

      const record = records[0];
      const clockOutTime = new Date().toISOString();
      const hoursWorked = _round((new Date(clockOutTime) - new Date(record.clockIn)) / 3600000);

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.CLOCK_RECORDS, record.id, {
        clockOut: clockOutTime, status: 'completed', hoursWorked
      });

      console.log(`[EnterpriseRosterService] Clock-out: ${staffId} — ${hoursWorked}h worked`);
      return { success: true, data: updated };
    } catch (error) {
      console.error('[EnterpriseRosterService] clockOut error:', error);
      return { success: false, error: error.message };
    }
  }

  async generateTimesheets(facilityId, payPeriodStart, payPeriodEnd) {
    try {
      console.log(`[EnterpriseRosterService] Generating timesheets: ${facilityId} ${payPeriodStart}→${payPeriodEnd}`);

      const clockRecords = LocalStorageService.find(STORAGE_KEYS.CLOCK_RECORDS,
        r => r.facilityId === facilityId && r.status === 'completed' && r.clockIn >= payPeriodStart && r.clockIn <= payPeriodEnd + 'T23:59:59'
      );

      const payPeriodId = `pp-${facilityId}-${payPeriodStart}-${payPeriodEnd}`;
      const timesheets = [];

      for (const record of clockRecords) {
        const clockInDate = new Date(record.clockIn);
        const clockOutDate = record.clockOut ? new Date(record.clockOut) : null;
        const hoursWorked = clockOutDate ? _round((clockOutDate - clockInDate) / 3600000) : 0;
        const breakHours = hoursWorked > 5 ? 0.5 : 0;
        const netHours = Math.max(0, hoursWorked - breakHours);
        const ordinaryHours = Math.min(netHours, 7.6);
        const overtimeHours = Math.max(0, netHours - 7.6);

        const ts = createTimesheetEntry({
          staffId: record.staffId,
          staffName: record.staffName || record.staffId,
          shiftId: record.shiftId,
          payPeriodId,
          clockInTime: record.clockIn,
          clockOutTime: record.clockOut,
          ordinaryHours,
          overtimeHours,
          status: TIMESHEET_STATUSES.auto_generated.code
        });

        const created = LocalStorageService.addItem(STORAGE_KEYS.TIMESHEETS, ts);
        timesheets.push(created);
      }

      console.log(`[EnterpriseRosterService] Generated ${timesheets.length} timesheets for period ${payPeriodStart}→${payPeriodEnd}`);
      return { success: true, data: { payPeriodId, timesheetCount: timesheets.length, timesheets } };
    } catch (error) {
      console.error('[EnterpriseRosterService] generateTimesheets error:', error);
      return { success: false, error: error.message };
    }
  }

  async getTimesheets(filters = {}) {
    try {
      let timesheets = LocalStorageService.get(STORAGE_KEYS.TIMESHEETS, []);
      if (filters.facilityId) timesheets = timesheets.filter(t => t.facilityId === filters.facilityId);
      if (filters.staffId) timesheets = timesheets.filter(t => t.staffId === filters.staffId);
      if (filters.payPeriodId) timesheets = timesheets.filter(t => t.payPeriodId === filters.payPeriodId);
      if (filters.status) timesheets = timesheets.filter(t => t.status === filters.status);
      timesheets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return { success: true, data: timesheets };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async approveTimesheet(timesheetId, approvedBy) {
    try {
      const ts = LocalStorageService.findById(STORAGE_KEYS.TIMESHEETS, timesheetId);
      if (!ts) return { success: false, error: 'Timesheet not found' };

      const statusDef = TIMESHEET_STATUSES[ts.status];
      if (statusDef && !statusDef.editable) return { success: false, error: `Cannot approve timesheet in status: ${ts.status}` };

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.TIMESHEETS, timesheetId, {
        status: TIMESHEET_STATUSES.approved.code,
        approvedBy,
        approvedAt: new Date().toISOString()
      });

      console.log(`[EnterpriseRosterService] Timesheet approved: ${timesheetId} by ${approvedBy}`);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async detectVariances(timesheetId) {
    try {
      const ts = LocalStorageService.findById(STORAGE_KEYS.TIMESHEETS, timesheetId);
      if (!ts) return { success: false, error: 'Timesheet not found' };

      const variances = [];
      if (ts.rosteredStart && ts.clockInTime) {
        const diff = (new Date(ts.clockInTime) - new Date(ts.rosteredStart)) / 60000;
        if (diff < -10) variances.push({ type: VARIANCE_TYPES.early_start.code, minutes: Math.abs(Math.round(diff)), label: VARIANCE_TYPES.early_start.label });
        else if (diff > 10) variances.push({ type: VARIANCE_TYPES.late_start.code, minutes: Math.round(diff), label: VARIANCE_TYPES.late_start.label });
      }

      if (ts.rosteredEnd && ts.clockOutTime) {
        const diff = (new Date(ts.clockOutTime) - new Date(ts.rosteredEnd)) / 60000;
        if (diff < -10) variances.push({ type: VARIANCE_TYPES.early_finish.code, minutes: Math.abs(Math.round(diff)), label: VARIANCE_TYPES.early_finish.label });
        else if (diff > 10) variances.push({ type: VARIANCE_TYPES.late_finish.code, minutes: Math.round(diff), label: VARIANCE_TYPES.late_finish.label });
      }

      if (!ts.clockInTime && !ts.clockOutTime && ts.rosteredStart) {
        variances.push({ type: VARIANCE_TYPES.no_show.code, minutes: 0, label: VARIANCE_TYPES.no_show.label });
      }

      if (ts.ordinaryHours > 5 && !ts.breakStartTime) {
        variances.push({ type: VARIANCE_TYPES.missed_break.code, minutes: 30, label: VARIANCE_TYPES.missed_break.label });
      }

      const totalVarianceMinutes = variances.reduce((s, v) => s + v.minutes, 0);
      const requiresApproval = variances.some(v => VARIANCE_TYPES[v.type]?.requiresApproval);

      if (variances.length > 0) {
        LocalStorageService.updateItem(STORAGE_KEYS.TIMESHEETS, timesheetId, {
          varianceType: variances[0].type,
          varianceMinutes: totalVarianceMinutes
        });
      }

      return { success: true, data: { timesheetId, variances, totalVarianceMinutes, requiresApproval } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getPayPeriodSummary(facilityId, payPeriodStart, payPeriodEnd) {
    try {
      const payPeriodId = `pp-${facilityId}-${payPeriodStart}-${payPeriodEnd}`;
      const timesheets = LocalStorageService.find(STORAGE_KEYS.TIMESHEETS, t => t.payPeriodId === payPeriodId);
      const clockRecords = LocalStorageService.find(STORAGE_KEYS.CLOCK_RECORDS,
        r => r.facilityId === facilityId && r.clockIn >= payPeriodStart && r.clockIn <= payPeriodEnd + 'T23:59:59'
      );

      const totalOrdinaryHours = timesheets.reduce((s, t) => s + (t.ordinaryHours || 0), 0);
      const totalOvertimeHours = timesheets.reduce((s, t) => s + (t.overtimeHours || 0), 0);
      const totalGross = timesheets.reduce((s, t) => s + (t.totalGross || 0), 0);
      const approved = timesheets.filter(t => t.status === TIMESHEET_STATUSES.approved.code).length;
      const pending = timesheets.filter(t => t.status !== TIMESHEET_STATUSES.approved.code && t.status !== TIMESHEET_STATUSES.exported.code).length;

      return {
        success: true,
        data: {
          facilityId, payPeriodId, period: { start: payPeriodStart, end: payPeriodEnd },
          timesheetCount: timesheets.length,
          clockRecordCount: clockRecords.length,
          totalOrdinaryHours: _round(totalOrdinaryHours),
          totalOvertimeHours: _round(totalOvertimeHours),
          totalHours: _round(totalOrdinaryHours + totalOvertimeHours),
          totalGross: _round(totalGross),
          approved, pending,
          readyForPayroll: pending === 0 && timesheets.length > 0
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = (x) => x * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  _getFacilityCoords(facilityId) {
    const coords = { sunshine: { lat: -37.789, lng: 144.922 }, bayview: { lat: -37.850, lng: 145.010 } };
    return coords[facilityId] || null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. Shift Marketplace
  // ═══════════════════════════════════════════════════════════════════════════

  async postShiftToMarketplace(shiftId, postedBy) {
    try {
      console.log(`[EnterpriseRosterService] Posting shift to marketplace: ${shiftId}`);
      const tierOrder = Object.values(SHIFT_MARKETPLACE_TIERS).sort((a, b) => a.priority - b.priority);
      const firstTier = tierOrder[0].code;

      const listing = createMarketplaceShift({
        shiftId, postedBy,
        tier: firstTier, currentTier: firstTier,
        expiresAt: new Date(Date.now() + 48 * 3600000).toISOString(),
        status: 'open'
      });

      const created = LocalStorageService.addItem(STORAGE_KEYS.MARKETPLACE, listing);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async applyForShift(marketplaceShiftId, staffId) {
    try {
      const listing = LocalStorageService.findById(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId);
      if (!listing) return { success: false, error: 'Marketplace listing not found' };
      if (listing.status !== 'open') return { success: false, error: `Shift is no longer open (status: ${listing.status})` };

      if (listing.applicants.some(a => a.staffId === staffId)) {
        return { success: false, error: 'Already applied for this shift' };
      }

      const hrResult = await HRService.getById(staffId);
      const staffName = hrResult.success ? `${hrResult.data.firstName} ${hrResult.data.lastName}` : staffId;
      const roleCode = hrResult.success ? hrResult.data.roleCode : null;

      listing.applicants.push({
        staffId, staffName, roleCode,
        appliedAt: new Date().toISOString(),
        eligible: true
      });

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId, {
        applicants: listing.applicants
      });

      console.log(`[EnterpriseRosterService] Staff ${staffName} applied for marketplace shift ${marketplaceShiftId}`);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async selectApplicant(marketplaceShiftId, staffId, selectedBy) {
    try {
      const listing = LocalStorageService.findById(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId);
      if (!listing) return { success: false, error: 'Marketplace listing not found' };

      const applicant = listing.applicants.find(a => a.staffId === staffId);
      if (!applicant) return { success: false, error: 'Applicant not found in listing' };

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId, {
        selectedStaffId: staffId, selectedBy, selectedAt: new Date().toISOString(),
        status: 'filled'
      });

      console.log(`[EnterpriseRosterService] Selected ${applicant.staffName} for marketplace shift ${marketplaceShiftId}`);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async escalateTier(marketplaceShiftId) {
    try {
      const listing = LocalStorageService.findById(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId);
      if (!listing) return { success: false, error: 'Marketplace listing not found' };

      const tierOrder = Object.values(SHIFT_MARKETPLACE_TIERS).sort((a, b) => a.priority - b.priority);
      const currentIdx = tierOrder.findIndex(t => t.code === listing.currentTier);

      if (currentIdx === -1 || currentIdx >= tierOrder.length - 1) {
        return { success: false, error: 'Already at highest tier — agency escalation required' };
      }

      const nextTier = tierOrder[currentIdx + 1];
      const updated = LocalStorageService.updateItem(STORAGE_KEYS.MARKETPLACE, marketplaceShiftId, {
        currentTier: nextTier.code,
        tierEscalatedAt: new Date().toISOString(),
        agencyOffered: nextTier.code === 'agency'
      });

      console.log(`[EnterpriseRosterService] Escalated marketplace ${marketplaceShiftId} to tier: ${nextTier.label}`);
      return { success: true, data: { ...updated, newTier: nextTier } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getMarketplaceShifts(filters = {}) {
    try {
      let listings = LocalStorageService.get(STORAGE_KEYS.MARKETPLACE, []);
      if (filters.facilityId) listings = listings.filter(l => l.facilityId === filters.facilityId);
      if (filters.status) listings = listings.filter(l => l.status === filters.status);
      if (filters.tier) listings = listings.filter(l => l.currentTier === filters.tier);
      if (filters.staffEligible) {
        listings = listings.filter(l => l.status === 'open');
      }
      listings.sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
      return { success: true, data: listings };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getMarketplaceAnalytics(facilityId, dateRange = {}) {
    try {
      let listings = LocalStorageService.get(STORAGE_KEYS.MARKETPLACE, []);
      if (facilityId) listings = listings.filter(l => l.facilityId === facilityId);

      const filled = listings.filter(l => l.status === 'filled');
      const open = listings.filter(l => l.status === 'open');
      const expired = listings.filter(l => l.status === 'expired');

      const avgTimeToFill = filled.length > 0
        ? _round(filled.reduce((sum, l) => sum + (new Date(l.selectedAt) - new Date(l.postedAt)) / 3600000, 0) / filled.length)
        : 0;

      const totalSavings = filled.reduce((sum, l) => sum + (l.costComparison?.savings || 0), 0);
      const agencyAverted = filled.filter(l => l.currentTier !== 'agency').length;

      return {
        success: true,
        data: {
          facilityId,
          totalListings: listings.length,
          open: open.length,
          filled: filled.length,
          expired: expired.length,
          fillRate: listings.length > 0 ? _round((filled.length / listings.length) * 100) : 0,
          avgTimeToFillHours: avgTimeToFill,
          avgApplicantsPerShift: listings.length > 0 ? _round(listings.reduce((s, l) => s + l.applicants.length, 0) / listings.length) : 0,
          agencyShiftsAverted: agencyAverted,
          totalSavingsVsAgency: _round(totalSavings),
          tierBreakdown: Object.values(SHIFT_MARKETPLACE_TIERS).map(t => ({
            tier: t.code, label: t.label,
            count: listings.filter(l => l.currentTier === t.code).length
          }))
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. On-Call & Escalation
  // ═══════════════════════════════════════════════════════════════════════════

  async createOnCallEntry(data) {
    try {
      const award = AWARD_DEFINITIONS.MA000034;
      const isWeekend = data.type === 'weekend';
      const allowanceDef = award.allowances.find(a => a.code === (isWeekend ? 'on_call_weekend' : 'on_call_weekday'));

      const entry = createOnCallEntry({
        ...data,
        allowanceRate: data.allowanceRate || allowanceDef?.amountPerShift || 0,
        status: 'scheduled'
      });

      const created = LocalStorageService.addItem(STORAGE_KEYS.ONCALL, entry);
      console.log(`[EnterpriseRosterService] On-call created: ${data.staffName || data.staffId} at ${data.facilityId}`);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async logCallOut(onCallId, callOutData) {
    try {
      const entry = LocalStorageService.findById(STORAGE_KEYS.ONCALL, onCallId);
      if (!entry) return { success: false, error: 'On-call entry not found' };

      const callOut = {
        id: _uid('cout'),
        calledAt: new Date().toISOString(),
        reason: callOutData.reason || '',
        arrivedAt: callOutData.arrivedAt || null,
        completedAt: callOutData.completedAt || null,
        hoursWorked: callOutData.hoursWorked || 0,
        overtimeApplicable: callOutData.overtimeApplicable ?? true,
        notes: callOutData.notes || ''
      };

      entry.callOuts.push(callOut);
      const updated = LocalStorageService.updateItem(STORAGE_KEYS.ONCALL, onCallId, {
        callOuts: entry.callOuts, status: 'active'
      });

      console.log(`[EnterpriseRosterService] Call-out logged for on-call ${onCallId}: ${callOutData.reason}`);
      return { success: true, data: { onCallEntry: updated, callOut } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getOnCallRoster(facilityId, dateRange = {}) {
    try {
      let entries = LocalStorageService.find(STORAGE_KEYS.ONCALL, e => e.facilityId === facilityId);
      if (dateRange.startDate) entries = entries.filter(e => e.endDateTime >= dateRange.startDate);
      if (dateRange.endDate) entries = entries.filter(e => e.startDateTime <= dateRange.endDate);
      entries.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
      return { success: true, data: entries };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async triggerEscalation(facilityId, reason) {
    try {
      console.log(`[EnterpriseRosterService] Escalation triggered at ${facilityId}: ${reason}`);

      const escalation = createEscalationEvent({ facilityId, reason, currentLevel: 1 });
      escalation.escalationChain[0].notifiedAt = new Date().toISOString();

      const created = LocalStorageService.addItem(STORAGE_KEYS.ESCALATIONS, escalation);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async respondToEscalation(escalationId, respondedBy, outcome) {
    try {
      const esc = LocalStorageService.findById(STORAGE_KEYS.ESCALATIONS, escalationId);
      if (!esc) return { success: false, error: 'Escalation not found' };

      const now = new Date().toISOString();
      const currentStep = esc.escalationChain.find(s => s.level === esc.currentLevel);
      if (currentStep) {
        currentStep.respondedAt = now;
        currentStep.respondedBy = respondedBy;
        currentStep.response = outcome;
      }

      const responseTimeMs = new Date(now) - new Date(esc.triggeredAt);
      const isResolved = outcome === 'resolved' || outcome === 'covered';

      const updates = {
        escalationChain: esc.escalationChain,
        respondedBy, respondedAt: now,
        responseTime: Math.round(responseTimeMs / 60000),
        outcome: isResolved ? outcome : 'escalated'
      };

      if (isResolved) {
        updates.resolvedAt = now;
      } else {
        const nextLevel = esc.currentLevel + 1;
        if (nextLevel <= ESCALATION_LEVELS.length) {
          updates.currentLevel = nextLevel;
          updates.escalationChain = esc.escalationChain;
          updates.escalationChain[nextLevel - 1].notifiedAt = now;
        }
      }

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.ESCALATIONS, escalationId, updates);
      console.log(`[EnterpriseRosterService] Escalation ${escalationId} responded: ${outcome} by ${respondedBy}`);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getEscalationHistory(facilityId) {
    try {
      const escalations = LocalStorageService.find(STORAGE_KEYS.ESCALATIONS, e => e.facilityId === facilityId);
      escalations.sort((a, b) => new Date(b.triggeredAt) - new Date(a.triggeredAt));

      const resolved = escalations.filter(e => e.resolvedAt);
      const avgResponseMinutes = resolved.length > 0
        ? _round(resolved.reduce((s, e) => s + (e.responseTime || 0), 0) / resolved.length)
        : 0;

      return {
        success: true,
        data: {
          facilityId,
          escalations,
          summary: {
            total: escalations.length,
            resolved: resolved.length,
            active: escalations.filter(e => !e.resolvedAt).length,
            avgResponseMinutes
          }
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. Acuity Demand Engine
  // ═══════════════════════════════════════════════════════════════════════════

  async calculateStaffingDemand(facilityId, date) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const wardResults = (facilityProfile.wardProfiles || []).map(ward => {
        const demand = calculateDemand({
          occupancy: ward.occupancy || 0,
          anaccDistribution: ward.acuityMix || null,
          clinicalEscalations: Math.floor(Math.random() * 2),
          oneToOneObservations: ward.wardName === 'Dementia Unit' ? 2 : 0,
          palliativeActive: Math.floor(Math.random() * 2),
          twoPersonAssists: Math.floor(ward.occupancy * 0.15),
          newAdmissions: Math.random() > 0.7 ? 1 : 0
        });

        return { wardId: ward.wardId, wardName: ward.wardName, occupancy: ward.occupancy, beds: ward.beds, ...demand };
      });

      const facilityTotals = wardResults.reduce((acc, w) => {
        acc.totalRequired += w.totalRequired;
        acc.requiredRN += w.requiredRN;
        acc.requiredEN += w.requiredEN;
        acc.requiredPCA += w.requiredPCA;
        acc.adjustedCareMinutes += w.adjustedCareMinutes;
        acc.adjustedRNMinutes += w.adjustedRNMinutes;
        return acc;
      }, { totalRequired: 0, requiredRN: 0, requiredEN: 0, requiredPCA: 0, adjustedCareMinutes: 0, adjustedRNMinutes: 0 });

      const result = { facilityId, date, facilityTotals, wardDemand: wardResults, minimumStaffPerShift: facilityProfile.minimumStaffPerShift, generatedAt: new Date().toISOString() };

      LocalStorageService.addItem(STORAGE_KEYS.DEMAND_CACHE, { id: `demand-${facilityId}-${date}`, ...result });

      return { success: true, data: result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getDemandDashboard(facilityId, startDate, endDate) {
    try {
      const days = [];
      const current = new Date(startDate);
      const end = new Date(endDate);

      while (current <= end) {
        const dateStr = _toDateStr(current);
        const demandResult = await this.calculateStaffingDemand(facilityId, dateStr);
        if (demandResult.success) {
          days.push({ date: dateStr, demand: demandResult.data.facilityTotals, wards: demandResult.data.wardDemand });
        }
        current.setDate(current.getDate() + 1);
      }

      return {
        success: true,
        data: {
          facilityId, period: { startDate, endDate },
          dailyDemand: days,
          avgDailyStaffRequired: days.length > 0 ? _round(days.reduce((s, d) => s + d.demand.totalRequired, 0) / days.length) : 0,
          peakStaffRequired: days.length > 0 ? Math.max(...days.map(d => d.demand.totalRequired)) : 0
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getDemandAlerts(facilityId) {
    try {
      const today = _toDateStr(new Date());
      const demandResult = await this.calculateStaffingDemand(facilityId, today);
      if (!demandResult.success) return demandResult;

      const alerts = [];
      const demand = demandResult.data;

      for (const ward of demand.wardDemand) {
        const minStaff = demand.minimumStaffPerShift;
        if (ward.requiredRN > (minStaff?.am?.rn || 2)) {
          alerts.push({
            severity: 'high', wardId: ward.wardId, wardName: ward.wardName,
            message: `RN demand (${ward.requiredRN}) exceeds baseline (${minStaff?.am?.rn || 2})`,
            type: 'understaffing'
          });
        }
        if (ward.requiredPCA > (minStaff?.am?.pca || 6)) {
          alerts.push({
            severity: 'medium', wardId: ward.wardId, wardName: ward.wardName,
            message: `PCA demand (${ward.requiredPCA}) exceeds baseline (${minStaff?.am?.pca || 6})`,
            type: 'understaffing'
          });
        }
      }

      return { success: true, data: { facilityId, date: today, alerts, alertCount: alerts.length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAcuityHeatmap(facilityId) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const shifts = ['AM', 'PM', 'NIGHT'];
      const heatmap = (facilityProfile.wardProfiles || []).map(ward => {
        const highNeedRatio = ward.acuityMix ? (ward.acuityMix.highNeed / Math.max(1, ward.occupancy)) : 0.3;
        return {
          wardId: ward.wardId,
          wardName: ward.wardName,
          occupancy: ward.occupancy,
          beds: ward.beds,
          occupancyRate: ward.beds > 0 ? _round((ward.occupancy / ward.beds) * 100) : 0,
          highNeedRatio: _round(highNeedRatio * 100),
          acuityLevel: highNeedRatio > 0.6 ? 'critical' : highNeedRatio > 0.4 ? 'high' : highNeedRatio > 0.25 ? 'medium' : 'low',
          shiftDemand: shifts.reduce((acc, shift) => {
            const demand = calculateDemand({ occupancy: ward.occupancy, anaccDistribution: ward.acuityMix });
            acc[shift] = { requiredRN: demand.requiredRN, requiredEN: demand.requiredEN, requiredPCA: demand.requiredPCA, total: demand.totalRequired };
            return acc;
          }, {})
        };
      });

      return { success: true, data: { facilityId, heatmap, generatedAt: new Date().toISOString() } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. Payroll Integration
  // ═══════════════════════════════════════════════════════════════════════════

  async generatePayRun(facilityId, payPeriodStart, payPeriodEnd) {
    try {
      console.log(`[EnterpriseRosterService] Generating pay run: ${facilityId} ${payPeriodStart}→${payPeriodEnd}`);

      const timesheets = LocalStorageService.find(STORAGE_KEYS.TIMESHEETS,
        t => t.payPeriodId === `pp-${facilityId}-${payPeriodStart}-${payPeriodEnd}` && t.status === TIMESHEET_STATUSES.approved.code
      );

      if (timesheets.length === 0) {
        const allTs = LocalStorageService.find(STORAGE_KEYS.TIMESHEETS, t => t.payPeriodId === `pp-${facilityId}-${payPeriodStart}-${payPeriodEnd}`);
        if (allTs.length > 0) return { success: false, error: `${allTs.length} timesheets exist but none are approved` };
      }

      const lineItems = timesheets.map(ts => {
        const payCalc = calculateAwardPay({
          awardCode: ts.awardApplied || 'MA000018',
          classificationLevel: ts.classificationLevel || 'PCA2',
          payPoint: ts.payPoint || 1,
          hoursWorked: (ts.ordinaryHours || 0) + (ts.overtimeHours || 0),
          shiftType: 'day',
          dayType: 'weekday',
          isOvertimeFirst2: ts.overtimeHours > 0 && ts.overtimeHours <= 2,
          isOvertimeAfter: ts.overtimeHours > 2
        });

        return {
          timesheetId: ts.id, staffId: ts.staffId, staffName: ts.staffName,
          ordinaryHours: ts.ordinaryHours, overtimeHours: ts.overtimeHours,
          ...payCalc
        };
      });

      const totals = lineItems.reduce((acc, li) => {
        acc.totalGross += li.totalGross;
        acc.totalSuper += li.superannuation;
        acc.totalEmployerCost += li.employerCost;
        acc.totalOrdinaryHours += li.ordinaryHours;
        acc.totalOvertimeHours += li.overtimeHours;
        return acc;
      }, { totalGross: 0, totalSuper: 0, totalEmployerCost: 0, totalOrdinaryHours: 0, totalOvertimeHours: 0 });

      for (const k of Object.keys(totals)) totals[k] = _round(totals[k]);

      const payRun = {
        id: _uid('payrun'),
        facilityId,
        payPeriod: { start: payPeriodStart, end: payPeriodEnd },
        status: 'generated',
        lineItems,
        lineItemCount: lineItems.length,
        ...totals,
        generatedAt: new Date().toISOString(),
        exportedAt: null, exportFormat: null
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.PAY_RUNS, payRun);
      console.log(`[EnterpriseRosterService] Pay run generated: ${payRun.id} — $${totals.totalGross} gross, ${lineItems.length} line items`);
      return { success: true, data: created };
    } catch (error) {
      console.error('[EnterpriseRosterService] generatePayRun error:', error);
      return { success: false, error: error.message };
    }
  }

  async previewPayRun(facilityId, payPeriodStart, payPeriodEnd) {
    try {
      const clockRecords = LocalStorageService.find(STORAGE_KEYS.CLOCK_RECORDS,
        r => r.facilityId === facilityId && r.status === 'completed' && r.clockIn >= payPeriodStart && r.clockIn <= payPeriodEnd + 'T23:59:59'
      );

      const staffSummary = {};
      for (const rec of clockRecords) {
        if (!staffSummary[rec.staffId]) {
          staffSummary[rec.staffId] = { staffId: rec.staffId, staffName: rec.staffName || rec.staffId, shifts: 0, totalHours: 0, estimatedGross: 0 };
        }
        const hours = rec.hoursWorked || (rec.clockOut ? _round((new Date(rec.clockOut) - new Date(rec.clockIn)) / 3600000) : 0);
        staffSummary[rec.staffId].shifts += 1;
        staffSummary[rec.staffId].totalHours += hours;
        staffSummary[rec.staffId].estimatedGross += _round(hours * 30);
      }

      const preview = Object.values(staffSummary);
      preview.forEach(s => { s.totalHours = _round(s.totalHours); s.estimatedGross = _round(s.estimatedGross); });

      return {
        success: true,
        data: {
          facilityId,
          period: { start: payPeriodStart, end: payPeriodEnd },
          staffCount: preview.length,
          totalShifts: preview.reduce((s, p) => s + p.shifts, 0),
          totalHours: _round(preview.reduce((s, p) => s + p.totalHours, 0)),
          estimatedGross: _round(preview.reduce((s, p) => s + p.estimatedGross, 0)),
          staffSummary: preview,
          isPreview: true
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async exportPayroll(payRunId, format = 'xero_csv') {
    try {
      const payRun = LocalStorageService.findById(STORAGE_KEYS.PAY_RUNS, payRunId);
      if (!payRun) return { success: false, error: 'Pay run not found' };

      const validFormats = ['xero_csv', 'myob_csv', 'keypay_api', 'adp_csv', 'custom_csv'];
      if (!validFormats.includes(format)) return { success: false, error: `Unsupported format: ${format}. Supported: ${validFormats.join(', ')}` };

      const headers = { xero_csv: 'EmpNo,FirstName,LastName,OrdinaryHours,OvertimeHours,GrossPay,Super', myob_csv: 'EmployeeNumber,Name,Hours,Overtime,Amount,Superannuation', keypay_api: 'id,employee_id,ordinary_hours,overtime_hours,gross,super_guarantee', adp_csv: 'WorkerID,Name,RegHours,OTHours,GrossWages,RetirementContribution', custom_csv: 'StaffID,StaffName,OrdinaryHours,OvertimeHours,BasePay,PenaltyPay,OvertimePay,CasualLoading,TotalGross,Super,EmployerCost' };

      const rows = payRun.lineItems.map((li, idx) => {
        const base = { empNo: idx + 1, staffId: li.staffId, name: li.staffName, ordinary: li.ordinaryHours, overtime: li.overtimeHours, gross: li.totalGross, super: li.superannuation, basePay: li.basePay, penaltyPay: li.penaltyPay, overtimePay: li.overtimePay, casualLoading: li.casualLoading, employerCost: li.employerCost };
        return base;
      });

      const updated = LocalStorageService.updateItem(STORAGE_KEYS.PAY_RUNS, payRunId, {
        status: 'exported', exportedAt: new Date().toISOString(), exportFormat: format
      });

      const timesheets = LocalStorageService.find(STORAGE_KEYS.TIMESHEETS,
        t => payRun.lineItems.some(li => li.timesheetId === t.id)
      );
      for (const ts of timesheets) {
        LocalStorageService.updateItem(STORAGE_KEYS.TIMESHEETS, ts.id, {
          status: TIMESHEET_STATUSES.exported.code,
          exportedAt: new Date().toISOString(),
          exportBatchId: payRunId
        });
      }

      console.log(`[EnterpriseRosterService] Payroll exported: ${payRunId} in ${format} format, ${rows.length} records`);
      return {
        success: true,
        data: {
          payRunId, format,
          header: headers[format],
          recordCount: rows.length,
          rows,
          exportedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getPayRunHistory(facilityId) {
    try {
      const payRuns = LocalStorageService.find(STORAGE_KEYS.PAY_RUNS, r => r.facilityId === facilityId);
      payRuns.sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt));
      return { success: true, data: payRuns };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. GPMS Reporting
  // ═══════════════════════════════════════════════════════════════════════════

  async generateCareMinutesReport(facilityId, month, year) {
    try {
      console.log(`[EnterpriseRosterService] Generating GPMS care minutes report: ${facilityId} ${month}/${year}`);
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const daysInMonth = new Date(year, month, 0).getDate();
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month).padStart(2, '0')}-${daysInMonth}`;
      const occupancy = facilityProfile.currentOccupancy;

      const targetTotalPerDay = facilityProfile.targetCareMinutesPerDay;
      const targetRNPerDay = facilityProfile.targetRNMinutesPerDay;
      const simulatedVariance = 0.85 + Math.random() * 0.3;
      const rnVariance = 0.80 + Math.random() * 0.35;

      const achievedTotalPerResidentPerDay = _round(targetTotalPerDay * simulatedVariance);
      const achievedRNPerResidentPerDay = _round(targetRNPerDay * rnVariance);
      const totalCareMinutesDelivered = Math.round(achievedTotalPerResidentPerDay * occupancy * daysInMonth);
      const totalRNMinutesDelivered = Math.round(achievedRNPerResidentPerDay * occupancy * daysInMonth);

      const report = {
        id: _uid('gpms'),
        facilityId,
        facilityName: facilityProfile.facilityName,
        reportingPeriod: { month, year, startDate, endDate, daysInMonth },
        occupancy,
        totalBeds: facilityProfile.totalBeds,
        occupancyRate: _round((occupancy / facilityProfile.totalBeds) * 100),
        careMinutes: {
          targetPerResidentPerDay: targetTotalPerDay,
          achievedPerResidentPerDay: achievedTotalPerResidentPerDay,
          totalDelivered: totalCareMinutesDelivered,
          compliant: achievedTotalPerResidentPerDay >= targetTotalPerDay,
          variance: _round(achievedTotalPerResidentPerDay - targetTotalPerDay)
        },
        rnMinutes: {
          targetPerResidentPerDay: targetRNPerDay,
          achievedPerResidentPerDay: achievedRNPerResidentPerDay,
          totalDelivered: totalRNMinutesDelivered,
          compliant: achievedRNPerResidentPerDay >= targetRNPerDay,
          variance: _round(achievedRNPerResidentPerDay - targetRNPerDay)
        },
        status: 'ready_for_review',
        generatedAt: new Date().toISOString()
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.GPMS_REPORTS, report);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async generateRNCoverageDeclaration(facilityId, month, year) {
    try {
      const daysInMonth = new Date(year, month, 0).getDate();
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month).padStart(2, '0')}-${daysInMonth}`;
      const shifts = ['AM', 'PM', 'NIGHT'];
      const totalSlots = daysInMonth * shifts.length;

      const coveredSlots = totalSlots - Math.floor(Math.random() * 3);
      const gaps = [];
      const gapCount = totalSlots - coveredSlots;
      for (let i = 0; i < gapCount; i++) {
        const gapDay = Math.floor(Math.random() * daysInMonth) + 1;
        const gapShift = shifts[Math.floor(Math.random() * shifts.length)];
        gaps.push({ date: `${year}-${String(month).padStart(2, '0')}-${String(gapDay).padStart(2, '0')}`, shift: gapShift, reason: 'Unplanned absence — backfill not achieved' });
      }

      const declaration = {
        id: _uid('rndecl'),
        facilityId, reportingPeriod: { month, year, startDate, endDate },
        totalShiftSlots: totalSlots,
        coveredSlots,
        gapSlots: gapCount,
        coverageRate: _round((coveredSlots / totalSlots) * 100),
        compliant: gapCount === 0,
        gaps,
        declarationStatement: gapCount === 0
          ? 'We declare that a registered nurse was on site and available 24 hours a day, 7 days a week, for the entire reporting period.'
          : `We declare that RN coverage was maintained for ${coveredSlots} of ${totalSlots} shift slots. ${gapCount} gap(s) occurred and are documented above.`,
        generatedAt: new Date().toISOString()
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.GPMS_REPORTS, declaration);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async generateNQIPWorkforceIndicators(facilityId, quarter, year) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      const occupancy = facilityProfile?.currentOccupancy || 60;

      const indicators = {
        id: _uid('nqip'),
        facilityId,
        reportingPeriod: { quarter, year },
        staffingIndicators: {
          totalCareMinutesPerResidentPerDay: _round(195 + Math.random() * 40),
          rnMinutesPerResidentPerDay: _round(38 + Math.random() * 12),
          agencyUsePercentage: _round(5 + Math.random() * 10),
          staffTurnoverRate: _round(15 + Math.random() * 15),
          casualUsageRate: _round(10 + Math.random() * 10),
          overtimePercentage: _round(3 + Math.random() * 5),
          sickLeaveRate: _round(3 + Math.random() * 4),
          workersCompClaimRate: _round(Math.random() * 3),
          mandatoryTrainingCompliance: _round(85 + Math.random() * 15),
          credentialComplianceRate: _round(90 + Math.random() * 10)
        },
        benchmarks: {
          sectorAvgCareMinutes: 210,
          sectorAvgRNMinutes: 42,
          sectorAvgAgencyUse: 12,
          sectorAvgTurnover: 25
        },
        generatedAt: new Date().toISOString()
      };

      const created = LocalStorageService.addItem(STORAGE_KEYS.GPMS_REPORTS, indicators);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async calculateStarRatingStaffing(facilityId) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const careMinAchieved = _round(facilityProfile.targetCareMinutesPerDay * (0.9 + Math.random() * 0.2));
      const rnMinAchieved = _round(facilityProfile.targetRNMinutesPerDay * (0.85 + Math.random() * 0.25));
      const rnCoverage24_7 = Math.random() > 0.1;

      let score = 0;
      if (careMinAchieved >= 215) score += 2;
      else if (careMinAchieved >= 200) score += 1;
      if (rnMinAchieved >= 44) score += 1.5;
      else if (rnMinAchieved >= 40) score += 0.75;
      if (rnCoverage24_7) score += 1.5;

      const maxScore = 5;
      const starPrediction = Math.min(5, Math.max(1, Math.round(score)));

      return {
        success: true,
        data: {
          facilityId,
          careMinutesAchieved: careMinAchieved,
          rnMinutesAchieved: rnMinAchieved,
          rnCoverage24_7,
          rawScore: _round(score),
          maxScore,
          predictedStarRating: starPrediction,
          ratingLabel: ['', 'Significant Improvement Needed', 'Improvement Needed', 'Acceptable', 'Good', 'Excellent'][starPrediction],
          recommendations: score < 3 ? [
            'Increase RN hours to meet 44 min/resident/day target',
            'Review roster for 24/7 RN coverage gaps',
            'Consider reducing agency reliance to improve continuity'
          ] : [],
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. Workforce Analytics
  // ═══════════════════════════════════════════════════════════════════════════

  async getOperationalMetrics(facilityId, dateRange = {}) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      const occupancy = facilityProfile?.currentOccupancy || 60;
      const beds = facilityProfile?.totalBeds || 90;

      const hrResult = await HRService.getAll(facilityId);
      const totalStaff = hrResult.success ? hrResult.data.length : 0;
      const agencyStaff = hrResult.success ? hrResult.data.filter(s => s.employmentType === 'agency').length : 0;

      const labourCostPerBedDay = _round(280 + Math.random() * 80);
      const agencyPercentage = totalStaff > 0 ? _round((agencyStaff / totalStaff) * 100) : 0;
      const overtimePercentage = _round(4 + Math.random() * 6);
      const careMinutesAchieved = _round(200 + Math.random() * 30);
      const rnMinutesAchieved = _round(38 + Math.random() * 12);
      const turnoverRateAnnual = _round(18 + Math.random() * 15);
      const sickLeaveRate = _round(3.5 + Math.random() * 3);
      const vacancyRate = _round(2 + Math.random() * 5);

      return {
        success: true,
        data: {
          facilityId,
          dateRange,
          occupancy,
          beds,
          occupancyRate: _round((occupancy / beds) * 100),
          totalStaff,
          labourCostPerBedDay,
          agencyPercentage,
          overtimePercentage,
          careMinutesPerResidentPerDay: careMinutesAchieved,
          rnMinutesPerResidentPerDay: rnMinutesAchieved,
          turnoverRateAnnual,
          sickLeaveRate,
          vacancyRate,
          staffToResidentRatio: occupancy > 0 ? `1:${_round(occupancy / Math.max(1, totalStaff))}` : 'N/A',
          costPerCareMinute: careMinutesAchieved > 0 ? _round(labourCostPerBedDay / careMinutesAchieved) : 0,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getExecutiveDashboard(organisationId) {
    try {
      const facilityProfiles = LocalStorageService.get(STORAGE_KEYS.FACILITY_PROFILES, []);

      const facilitySummaries = [];
      for (const fp of facilityProfiles) {
        const metrics = await this.getOperationalMetrics(fp.facilityId);
        if (metrics.success) {
          facilitySummaries.push({ facilityId: fp.facilityId, facilityName: fp.facilityName, ...metrics.data });
        }
      }

      const totalBeds = facilitySummaries.reduce((s, f) => s + f.beds, 0);
      const totalOccupancy = facilitySummaries.reduce((s, f) => s + f.occupancy, 0);
      const totalStaff = facilitySummaries.reduce((s, f) => s + f.totalStaff, 0);
      const avgLabourCost = facilitySummaries.length > 0 ? _round(facilitySummaries.reduce((s, f) => s + f.labourCostPerBedDay, 0) / facilitySummaries.length) : 0;
      const avgCareMinutes = facilitySummaries.length > 0 ? _round(facilitySummaries.reduce((s, f) => s + f.careMinutesPerResidentPerDay, 0) / facilitySummaries.length) : 0;

      return {
        success: true,
        data: {
          organisationId: organisationId || 'medfast',
          facilityCount: facilitySummaries.length,
          totalBeds, totalOccupancy, totalStaff,
          overallOccupancyRate: totalBeds > 0 ? _round((totalOccupancy / totalBeds) * 100) : 0,
          avgLabourCostPerBedDay: avgLabourCost,
          avgCareMinutesPerResidentPerDay: avgCareMinutes,
          facilities: facilitySummaries,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getTurnoverPrediction(facilityId) {
    try {
      const hrResult = await HRService.getAll(facilityId);
      if (!hrResult.success) return hrResult;

      const predictions = hrResult.data.map(staff => {
        const tenure = staff.startDate ? _round((Date.now() - new Date(staff.startDate)) / (365.25 * 24 * 3600000)) : 1;
        let riskScore = 0.1;

        if (tenure < 1) riskScore += 0.25;
        else if (tenure < 2) riskScore += 0.15;
        if (staff.employmentType === 'casual') riskScore += 0.2;
        if (staff.employmentType === 'agency') riskScore += 0.35;

        const expiredTraining = (staff.trainingRecords || []).filter(t => t.status === 'expired').length;
        if (expiredTraining > 2) riskScore += 0.1;

        riskScore = Math.min(0.95, riskScore + (Math.random() * 0.15));

        return {
          staffId: staff.id,
          staffName: `${staff.firstName} ${staff.lastName}`,
          roleCode: staff.roleCode,
          employmentType: staff.employmentType,
          tenureYears: tenure,
          turnoverRisk: _round(riskScore),
          riskLevel: riskScore > 0.7 ? 'high' : riskScore > 0.4 ? 'medium' : 'low',
          factors: [
            ...(tenure < 1 ? ['Short tenure (<1 year)'] : []),
            ...(staff.employmentType === 'agency' ? ['Agency worker'] : []),
            ...(staff.employmentType === 'casual' ? ['Casual employment'] : []),
            ...(expiredTraining > 2 ? ['Multiple expired training records'] : [])
          ]
        };
      });

      predictions.sort((a, b) => b.turnoverRisk - a.turnoverRisk);
      const highRisk = predictions.filter(p => p.riskLevel === 'high');

      return {
        success: true,
        data: {
          facilityId,
          predictions,
          summary: {
            totalStaff: predictions.length,
            highRisk: highRisk.length,
            mediumRisk: predictions.filter(p => p.riskLevel === 'medium').length,
            lowRisk: predictions.filter(p => p.riskLevel === 'low').length,
            avgTurnoverRisk: predictions.length > 0 ? _round(predictions.reduce((s, p) => s + p.turnoverRisk, 0) / predictions.length) : 0,
            estimatedAnnualTurnover: highRisk.length
          },
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getWorkforceAnalytics(facilityId, dateRange = {}) {
    try {
      const [metricsResult, turnoverResult, demandResult, marketplaceResult, leaveResult] = await Promise.all([
        this.getOperationalMetrics(facilityId, dateRange),
        this.getTurnoverPrediction(facilityId),
        this.calculateStaffingDemand(facilityId, _toDateStr(new Date())),
        this.getMarketplaceAnalytics(facilityId, dateRange),
        this.getLeaveRequests({ facilityId })
      ]);

      return {
        success: true,
        data: {
          facilityId, dateRange,
          operationalMetrics: metricsResult.success ? metricsResult.data : null,
          turnoverPrediction: turnoverResult.success ? turnoverResult.data.summary : null,
          staffingDemand: demandResult.success ? demandResult.data.facilityTotals : null,
          marketplaceActivity: marketplaceResult.success ? marketplaceResult.data : null,
          leaveOverview: leaveResult.success ? {
            totalRequests: leaveResult.data.length,
            pending: leaveResult.data.filter(r => r.status === 'pending').length,
            approved: leaveResult.data.filter(r => r.status === 'approved').length
          } : null,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 9. Demand Forecasting
  // ═══════════════════════════════════════════════════════════════════════════

  async getShortTermForecast(facilityId) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const weeks = [];
      const today = new Date();
      for (let w = 0; w < 4; w++) {
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() + (w * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const occupancyTrend = facilityProfile.currentOccupancy + Math.floor((Math.random() - 0.3) * 3);
        const demand = calculateDemand({ occupancy: Math.max(0, occupancyTrend) });

        weeks.push({
          week: w + 1,
          startDate: _toDateStr(weekStart),
          endDate: _toDateStr(weekEnd),
          projectedOccupancy: Math.max(0, Math.min(facilityProfile.totalBeds, occupancyTrend)),
          demand,
          leaveCount: Math.floor(Math.random() * 4),
          marketplaceShiftsNeeded: Math.floor(Math.random() * 3),
          confidence: _round(0.95 - (w * 0.05))
        });
      }

      return {
        success: true,
        data: {
          facilityId, forecastType: 'short_term', horizonWeeks: 4,
          weeks,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getMediumTermForecast(facilityId) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const weeks = [];
      const today = new Date();
      for (let w = 0; w < 12; w++) {
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() + (w * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const seasonalFactor = 1 + Math.sin((weekStart.getMonth() / 12) * Math.PI * 2) * 0.05;
        const occupancyProjection = Math.round(facilityProfile.currentOccupancy * seasonalFactor + (Math.random() - 0.5) * 4);
        const demand = calculateDemand({ occupancy: Math.max(0, occupancyProjection) });

        weeks.push({
          week: w + 1,
          startDate: _toDateStr(weekStart),
          endDate: _toDateStr(weekEnd),
          projectedOccupancy: Math.max(0, Math.min(facilityProfile.totalBeds, occupancyProjection)),
          demand,
          estimatedCost: _round(demand.totalRequired * 8 * 35 * 7),
          confidence: _round(Math.max(0.5, 0.95 - (w * 0.035)))
        });
      }

      return {
        success: true,
        data: {
          facilityId, forecastType: 'medium_term', horizonWeeks: 12,
          weeks,
          avgProjectedOccupancy: weeks.length > 0 ? _round(weeks.reduce((s, w) => s + w.projectedOccupancy, 0) / weeks.length) : 0,
          avgStaffRequired: weeks.length > 0 ? _round(weeks.reduce((s, w) => s + w.demand.totalRequired, 0) / weeks.length) : 0,
          totalEstimatedCost: _round(weeks.reduce((s, w) => s + w.estimatedCost, 0)),
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async runScenario(facilityId, scenarioParams = {}) {
    try {
      const facilityProfile = LocalStorageService.find(STORAGE_KEYS.FACILITY_PROFILES, p => p.facilityId === facilityId)[0];
      if (!facilityProfile) return { success: false, error: 'Facility profile not found' };

      const baseOccupancy = scenarioParams.occupancy ?? facilityProfile.currentOccupancy;
      const baseHighNeed = scenarioParams.highNeedCount ?? Math.round(baseOccupancy * 0.4);
      const baseLowNeed = baseOccupancy - baseHighNeed;

      const baseDemand = calculateDemand({
        occupancy: baseOccupancy,
        anaccDistribution: { highNeed: baseHighNeed, lowNeed: baseLowNeed },
        clinicalEscalations: scenarioParams.clinicalEscalations ?? 0,
        oneToOneObservations: scenarioParams.oneToOneObservations ?? 0,
        palliativeActive: scenarioParams.palliativeActive ?? 0,
        twoPersonAssists: scenarioParams.twoPersonAssists ?? 0,
        newAdmissions: scenarioParams.newAdmissions ?? 0
      });

      const estimatedDailyCost = _round(baseDemand.totalRequired * 8 * 35);
      const estimatedWeeklyCost = _round(estimatedDailyCost * 7);

      const currentDemand = calculateDemand({ occupancy: facilityProfile.currentOccupancy });
      const staffDiff = baseDemand.totalRequired - currentDemand.totalRequired;

      return {
        success: true,
        data: {
          facilityId,
          scenarioParams,
          baselineOccupancy: facilityProfile.currentOccupancy,
          scenarioOccupancy: baseOccupancy,
          demand: baseDemand,
          currentDemand,
          staffDifference: staffDiff,
          estimatedDailyCost,
          estimatedWeeklyCost,
          estimatedAnnualCost: _round(estimatedWeeklyCost * 52),
          costDifference: _round((baseDemand.totalRequired - currentDemand.totalRequired) * 8 * 35 * 7),
          careMinutesPerResident: baseOccupancy > 0 ? _round(baseDemand.adjustedCareMinutes / baseOccupancy) : 0,
          rnMinutesPerResident: baseOccupancy > 0 ? _round(baseDemand.adjustedRNMinutes / baseOccupancy) : 0,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 10. Multi-Facility
  // ═══════════════════════════════════════════════════════════════════════════

  async getMultiFacilityDashboard(organisationId) {
    try {
      const facilityProfiles = LocalStorageService.get(STORAGE_KEYS.FACILITY_PROFILES, []);
      const dashboards = [];

      for (const fp of facilityProfiles) {
        const [metrics, demand, marketplace, starRating] = await Promise.all([
          this.getOperationalMetrics(fp.facilityId),
          this.calculateStaffingDemand(fp.facilityId, _toDateStr(new Date())),
          this.getMarketplaceAnalytics(fp.facilityId),
          this.calculateStarRatingStaffing(fp.facilityId)
        ]);

        dashboards.push({
          facilityId: fp.facilityId,
          facilityName: fp.facilityName,
          region: fp.region,
          beds: fp.totalBeds,
          occupancy: fp.currentOccupancy,
          occupancyRate: fp.totalBeds > 0 ? _round((fp.currentOccupancy / fp.totalBeds) * 100) : 0,
          operationalMetrics: metrics.success ? metrics.data : null,
          staffingDemand: demand.success ? demand.data.facilityTotals : null,
          marketplaceActivity: marketplace.success ? { openShifts: marketplace.data.open, fillRate: marketplace.data.fillRate } : null,
          starRating: starRating.success ? { predicted: starRating.data.predictedStarRating, label: starRating.data.ratingLabel } : null
        });
      }

      return {
        success: true,
        data: {
          organisationId: organisationId || 'medfast',
          facilityCount: dashboards.length,
          facilities: dashboards,
          aggregates: {
            totalBeds: dashboards.reduce((s, f) => s + f.beds, 0),
            totalOccupancy: dashboards.reduce((s, f) => s + f.occupancy, 0),
            avgOccupancyRate: dashboards.length > 0 ? _round(dashboards.reduce((s, f) => s + f.occupancyRate, 0) / dashboards.length) : 0,
            avgStarRating: dashboards.length > 0 ? _round(dashboards.reduce((s, f) => s + (f.starRating?.predicted || 0), 0) / dashboards.length) : 0
          },
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getFloatPoolStaff(organisationId) {
    try {
      const hrResult = await HRService.getAll();
      if (!hrResult.success) return hrResult;

      const floatPool = hrResult.data.filter(s => s.employmentType === 'casual' || s.employmentType === 'agency' || s.status === 'active');
      const floatStaff = floatPool.map(s => ({
        staffId: s.id,
        staffName: `${s.firstName} ${s.lastName}`,
        roleCode: s.roleCode,
        roleName: s.roleName,
        employmentType: s.employmentType,
        facilityId: s.facilityId,
        facilityName: s.facilityName,
        isAvailableToday: Math.random() > 0.4,
        lastShiftDate: _toDateStr(new Date(Date.now() - Math.floor(Math.random() * 7) * 86400000))
      }));

      return {
        success: true,
        data: {
          organisationId: organisationId || 'medfast',
          totalFloatStaff: floatStaff.length,
          availableToday: floatStaff.filter(s => s.isAvailableToday).length,
          staff: floatStaff,
          byRole: floatStaff.reduce((acc, s) => {
            acc[s.roleCode] = (acc[s.roleCode] || 0) + 1;
            return acc;
          }, {})
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getCrossFacilityAvailability(roleCode, date, shift) {
    try {
      const hrResult = await HRService.getAll();
      if (!hrResult.success) return hrResult;

      const eligible = hrResult.data.filter(s => s.roleCode === roleCode && s.status === 'active');

      const availability = eligible.map(s => ({
        staffId: s.id,
        staffName: `${s.firstName} ${s.lastName}`,
        roleCode: s.roleCode,
        currentFacilityId: s.facilityId,
        currentFacilityName: s.facilityName,
        employmentType: s.employmentType,
        isAvailable: Math.random() > 0.5,
        travelRequired: true,
        estimatedTravelKm: _round(5 + Math.random() * 30),
        travelAllowance: _round((5 + Math.random() * 30) * 0.91)
      }));

      const available = availability.filter(a => a.isAvailable);

      return {
        success: true,
        data: {
          roleCode, date, shift,
          totalEligible: eligible.length,
          available: available.length,
          staff: available.sort((a, b) => a.estimatedTravelKm - b.estimatedTravelKm)
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new EnterpriseRosterService();
